import { Alert, Grid } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useStore } from '../../../Provider'
import { OPLoadingCircle } from '../../OPcomponents/OPLoadingcircle'
import './CV.scss'

// Import subcomponents
import { CertificatesCard } from './components/CertificatesCard'
import { CertificatesPPTXCard } from './components/CertificatesPPTXCard'
import { CVBasicSettings } from './components/CVBasicSettings'
import { CVDownloadButton } from './components/CVDownloadButton'
import { CVSettingsProvider, useCVSettings } from './components/CVSettingsContext'
import { EducationCard } from './components/EducationCard'
import { EducationPPTXCard } from './components/EducationPPTXCard'
import { ExpertiseCard } from './components/ExpertiseCard'
import { ExpertisePPTXCard } from './components/ExpertisePPTXCard'
import { KeySkillsCard } from './components/KeySkillsCard'
import { LanguageSkillsCard } from './components/LanguageSkillsCard'
import { PersonalDetailsCard } from './components/PersonalDetailsCard'
import { PersonalDetailsPPTXCard } from './components/PersonalDetailsPPTXCard'
import { ProjectHistoryCard } from './components/ProjectHistoryCard'
import { ProjectHistoryPPTXCard } from './components/ProjectHistoryPPTXCard'
import { WorkHistoryCard } from './components/WorkHistoryCard'
import { WorkHistoryPPTXCard } from './components/WorkHistoryPPTXCard'

interface IParams {
  userId: string
}

const CVContent = () => {
  const { state } = useCVSettings()
  const isPowerPoint = state.cv_base === 'pptx'

  return (
    <>
      <Grid item xs={12} md={10} lg={8}>
        {isPowerPoint ? <PersonalDetailsPPTXCard /> : <PersonalDetailsCard />}
      </Grid>

      <Grid item xs={12} md={10} lg={8}>
        {isPowerPoint ? <ExpertisePPTXCard /> : <ExpertiseCard />}
      </Grid>

      <Grid item xs={12} md={10} lg={8}>
        {isPowerPoint ? <EducationPPTXCard /> : <EducationCard />}
      </Grid>

      <Grid item xs={12} md={10} lg={8}>
        {isPowerPoint ? <CertificatesPPTXCard /> : <CertificatesCard />}
      </Grid>

      <Grid item xs={12} md={10} lg={8}>
        {isPowerPoint ? <WorkHistoryPPTXCard /> : <WorkHistoryCard />}
      </Grid>

      <Grid item xs={12} md={10} lg={8}>
        {isPowerPoint ? <ProjectHistoryPPTXCard /> : <ProjectHistoryCard />}
      </Grid>

      {!isPowerPoint && (
        <>
          <Grid item xs={12} md={10} lg={8}>
            <KeySkillsCard />
          </Grid>

          <Grid item xs={12} md={10} lg={8}>
            <LanguageSkillsCard />
          </Grid>
        </>
      )}
    </>
  )
}

const PowerPointNotice = () => {
  const { t } = useTranslation()
  const { state } = useCVSettings()
  const isPowerPoint = state.cv_base === 'pptx'

  if (!isPowerPoint) return null

  return (
    <Grid item xs={12} md={10} lg={8}>
      <Alert severity='info'>{t('CVTemplates.PowerPointAutoSizeNotice')}</Alert>
    </Grid>
  )
}

const CVOverview = () => {
  const rootStore = useStore()
  const { validatedUser } = rootStore.loginStore
  const { getCVInfo, PersonCVInfo, loadingCVInfo, resetCVInfo } = rootStore.personStore
  const { personProfileOverview, getUserProfileOverview } = rootStore.profileStore
  const { userId } = useParams<IParams>()

  useEffect(() => {
    return () => {
      resetCVInfo()
    }
  }, [resetCVInfo])

  useEffect(() => {
    const targetUserId = userId ? Number(userId) : validatedUser
    if (targetUserId !== personProfileOverview?.id) {
      getUserProfileOverview(targetUserId)
    }
  }, [getUserProfileOverview, personProfileOverview?.id, userId, validatedUser])

  useEffect(() => {
    if (userId !== undefined) {
      getCVInfo(Number(userId))
    } else {
      getCVInfo(validatedUser)
    }
  }, [getCVInfo, validatedUser, userId])

  return (
    <>
      {!loadingCVInfo && PersonCVInfo.personDetails !== undefined ? (
        <CVSettingsProvider>
          <Grid container rowSpacing={4}>
            <Grid item xs={12}>
              <Grid container justifyContent={'space-between'}>
                <Grid>
                  <span className='profile--titlebigfont'>CV - {PersonCVInfo.personDetails.name}</span>
                </Grid>
                <Grid>
                  <CVDownloadButton variant='TOP' />
                </Grid>
                <hr className='profile--form__editdivider' />
              </Grid>

              <Grid spacing={2} container className='topPadding' justifyContent='center'>
                <PowerPointNotice />
                <Grid item xs={12} md={10} lg={8}>
                  <CVBasicSettings />
                </Grid>

                <CVContent />

                <Grid item xs={12} md={10} lg={8} sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                  <CVDownloadButton variant='BOTTOM' />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CVSettingsProvider>
      ) : (
        <OPLoadingCircle />
      )}
    </>
  )
}

export default observer(CVOverview)
